'use client';
import Head from 'next/head';
import { useState, useRef } from 'react';
import { ChartScatter } from 'lucide-react';
import MethodPageLayout from '@/components/layouts/MethodPageLayout';
import ScatterVisualiser from '@/components/methods/ScatterVisualiser';
import ColorSwatchRow from '@/components/common/ColorSwatchRow';
import SegmentedControl from '@/components/common/SegmentedControl';
import { ScatterPoint } from '@/lib/visualisers/scatter';

const SCATTER_COLORS = ['#185FA5', '#0F6E56', '#534AB7', '#993C1D', '#854F0B', '#f4a261'];
const DEFAULT_COLOR = '#185FA5';
const DEFAULT_POINTS: ScatterPoint[] = [
    { x: 1, y: 2, color: '#185FA5' },
    { x: 3, y: 5, color: '#0F6E56' },
    { x: 5, y: 3, color: '#534AB7' },
    { x: 7, y: 8, color: '#993C1D' },
];

export default function ScatterGraphPage() {
    const [points, setPoints] = useState<ScatterPoint[]>(DEFAULT_POINTS);
    const [showGrid, setShowGrid] = useState(true);
    const [color, setColor] = useState(DEFAULT_COLOR);
    const [inputX, setInputX] = useState('');
    const [inputY, setInputY] = useState('');
    const [inputLabel, setInputLabel] = useState('');
    const [error, setError] = useState('');
    const [xLabel, setXLabel] = useState('x');
    const [yLabel, setYLabel] = useState('y');
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [editX, setEditX] = useState('');
    const [editY, setEditY] = useState('');
    const [editLabel, setEditLabel] = useState('');
    const xRef = useRef<HTMLInputElement>(null);

    const addPoint = () => {
        const x = parseFloat(inputX);
        const y = parseFloat(inputY);
        if (isNaN(x) || isNaN(y)) {
            setError('Enter valid numbers for X and Y.');
            return;
        }
        setError('');
        setPoints((prev) => [...prev, { x, y, color, label: inputLabel.trim() || undefined }]);
        setInputX('');
        setInputY('');
        setInputLabel('');
        xRef.current?.focus();
    };

    const removePoint = (index: number) => {
        if (editingIndex === index) setEditingIndex(null);
        setPoints((prev) => prev.filter((_, i) => i !== index));
    };

    const startEdit = (index: number) => {
        setEditingIndex(index);
        setEditX(points[index].x.toString());
        setEditY(points[index].y.toString());
        setEditLabel(points[index].label ?? '');
    };

    const confirmEdit = () => {
        if (editingIndex === null) return;
        const x = parseFloat(editX);
        const y = parseFloat(editY);
        if (isNaN(x) || isNaN(y)) return;
        setPoints((prev) =>
            prev.map((p, i) =>
                i === editingIndex ? { ...p, x, y, label: editLabel.trim() || undefined } : p,
            ),
        );
        setEditingIndex(null);
    };

    const cancelEdit = () => setEditingIndex(null);

    const onReset = () => {
        setPoints(DEFAULT_POINTS);
        setShowGrid(true);
        setColor(DEFAULT_COLOR);
        setInputX('');
        setInputY('');
        setInputLabel('');
        setError('');
        setXLabel('x');
        setYLabel('y');
        setEditingIndex(null);
    };

    const inputCls =
        'w-full rounded-sm border-[0.5px] border-line bg-page px-2 py-[5px] text-[13px]';
    const sectionLabelCls =
        'text-[11px] font-medium uppercase tracking-[0.05em] text-muted';

    const controls = (
        <>
            {/* Add point */}
            <div className="flex flex-col gap-2">
                <label className={sectionLabelCls}>Add Point</label>
                <div className="grid grid-cols-2 gap-2">
                    <div className="flex flex-col gap-1">
                        <span className="text-[11px] text-hint">X</span>
                        <input
                            ref={xRef}
                            type="number"
                            value={inputX}
                            onChange={(e) => {
                                setInputX(e.target.value);
                                setError('');
                            }}
                            onKeyDown={(e) => e.key === 'Enter' && addPoint()}
                            placeholder="0"
                            className={inputCls}
                        />
                    </div>
                    <div className="flex flex-col gap-1">
                        <span className="text-[11px] text-hint">Y</span>
                        <input
                            type="number"
                            value={inputY}
                            onChange={(e) => {
                                setInputY(e.target.value);
                                setError('');
                            }}
                            onKeyDown={(e) => e.key === 'Enter' && addPoint()}
                            placeholder="0"
                            className={inputCls}
                        />
                    </div>
                </div>
                <div className="flex flex-col gap-1">
                    <span className="text-[11px] text-hint">
                        Label <span className="italic text-hint">(optional)</span>
                    </span>
                    <input
                        type="text"
                        value={inputLabel}
                        onChange={(e) => setInputLabel(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && addPoint()}
                        placeholder="e.g. A"
                        className={inputCls}
                    />
                </div>

                {error && <p className="text-[11px] text-coral">{error}</p>}

                <button
                    onClick={addPoint}
                    className="w-full rounded-sm border-none bg-teal-soft px-3 py-[7px] text-[13px] font-medium text-teal-ink"
                >
                    + Add point
                </button>
            </div>

            <ColorSwatchRow
                label="Point colour"
                colors={SCATTER_COLORS}
                value={color}
                onChange={setColor}
            />

            <SegmentedControl
                label="Grid"
                options={['On', 'Off']}
                value={showGrid ? 'On' : 'Off'}
                onChange={(v) => setShowGrid(v === 'On')}
            />

            {/* Axis labels */}
            <div className="flex flex-col gap-2">
                <label className={sectionLabelCls}>Axis Labels</label>
                <div className="grid grid-cols-2 gap-2">
                    <div className="flex flex-col gap-1">
                        <span className="text-[11px] text-hint">X axis</span>
                        <input
                            type="text"
                            value={xLabel}
                            onChange={(e) => setXLabel(e.target.value)}
                            placeholder="x"
                            className={inputCls}
                        />
                    </div>
                    <div className="flex flex-col gap-1">
                        <span className="text-[11px] text-hint">Y axis</span>
                        <input
                            type="text"
                            value={yLabel}
                            onChange={(e) => setYLabel(e.target.value)}
                            placeholder="y"
                            className={inputCls}
                        />
                    </div>
                </div>
            </div>

            {/* Points list */}
            {points.length > 0 && (
                <div className="flex flex-col gap-1.5">
                    <div className="flex items-center justify-between">
                        <label className={sectionLabelCls}>Points ({points.length})</label>
                        <button
                            onClick={() => {
                                setPoints([]);
                                setEditingIndex(null);
                            }}
                            className="border-none bg-transparent p-0 text-[11px] text-hint"
                        >
                            Clear all
                        </button>
                    </div>
                    <div className="flex max-h-[200px] flex-col gap-1 overflow-y-auto">
                        {points.map((pt, i) =>
                            editingIndex === i ? (
                                /* Edit mode row */
                                <div
                                    key={i}
                                    className="flex flex-col gap-1.5 rounded-sm border-[0.5px] border-line bg-blue-soft p-2"
                                >
                                    <div className="grid grid-cols-2 gap-1.5">
                                        <div className="flex flex-col gap-[3px]">
                                            <span className="text-[10px] text-hint">X</span>
                                            <input
                                                type="number"
                                                value={editX}
                                                autoFocus
                                                onChange={(e) => setEditX(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') confirmEdit();
                                                    if (e.key === 'Escape') cancelEdit();
                                                }}
                                                className={`${inputCls} text-xs`}
                                            />
                                        </div>
                                        <div className="flex flex-col gap-[3px]">
                                            <span className="text-[10px] text-hint">Y</span>
                                            <input
                                                type="number"
                                                value={editY}
                                                onChange={(e) => setEditY(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') confirmEdit();
                                                    if (e.key === 'Escape') cancelEdit();
                                                }}
                                                className={`${inputCls} text-xs`}
                                            />
                                        </div>
                                    </div>
                                    <div className="flex flex-col gap-[3px]">
                                        <span className="text-[10px] text-hint">
                                            Label (optional)
                                        </span>
                                        <input
                                            type="text"
                                            value={editLabel}
                                            onChange={(e) => setEditLabel(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') confirmEdit();
                                                if (e.key === 'Escape') cancelEdit();
                                            }}
                                            placeholder="e.g. A"
                                            className={`${inputCls} text-xs`}
                                        />
                                    </div>
                                    <div className="flex gap-[5px]">
                                        <button
                                            onClick={confirmEdit}
                                            className="flex-1 rounded-sm border-none bg-teal-soft py-[5px] text-xs font-medium text-teal-ink"
                                        >
                                            Save
                                        </button>
                                        <button
                                            onClick={cancelEdit}
                                            className="flex-1 rounded-sm border-[0.5px] border-line bg-transparent py-[5px] text-xs text-muted"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                /* View mode row */
                                <div
                                    key={i}
                                    className="flex items-center justify-between rounded-sm bg-page px-2 py-[5px] text-xs"
                                >
                                    <div className="flex items-center gap-[7px]">
                                        <span
                                            className="h-2 w-2 shrink-0 rounded-full"
                                            style={{ background: pt.color }}
                                        />
                                        <span className="tabular-nums text-ink">
                                            {pt.label ? (
                                                <>
                                                    <span className="font-medium">{pt.label}</span>
                                                    <span className="ml-1 text-hint">
                                                        ({fmtNum(pt.x)}, {fmtNum(pt.y)})
                                                    </span>
                                                </>
                                            ) : (
                                                <>
                                                    ({fmtNum(pt.x)}, {fmtNum(pt.y)})
                                                </>
                                            )}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-0.5">
                                        <button
                                            onClick={() => startEdit(i)}
                                            aria-label={`Edit point (${pt.x}, ${pt.y})`}
                                            className="border-none bg-transparent px-1 py-0.5 text-xs leading-none text-hint"
                                        >
                                            ✎
                                        </button>
                                        <button
                                            onClick={() => removePoint(i)}
                                            aria-label={`Remove point (${pt.x}, ${pt.y})`}
                                            className="border-none bg-transparent px-[3px] py-0.5 text-sm leading-none text-hint"
                                        >
                                            ×
                                        </button>
                                    </div>
                                </div>
                            ),
                        )}
                    </div>
                </div>
            )}

            <div className="flex flex-col gap-1.5 border-t-[0.5px] border-line pt-3.5">
                <p className="text-[11px] font-medium uppercase tracking-[0.05em] text-hint">
                    About
                </p>
                <p className="text-[13px] leading-relaxed text-muted">
                    A scatter graph plots individual data points on a coordinate grid. Each point
                    represents a pair of values (x, y).
                </p>
            </div>
        </>
    );

    return (
        <>
            <Head>
                <title>Scatter Graph</title>
            </Head>
            <MethodPageLayout
                icon={<ChartScatter size={20} />}
                iconColor="teal"
                title="Scatter graph"
                description="Plot and visualise data points on a coordinate grid."
                category="visualiser"
                controls={controls}
                onReset={onReset}
            >
                <ScatterVisualiser
                    points={points}
                    showGrid={showGrid}
                    xLabel={xLabel}
                    yLabel={yLabel}
                />
            </MethodPageLayout>
        </>
    );
}

function fmtNum(n: number): string {
    if (Number.isInteger(n)) return n.toString();
    return parseFloat(n.toFixed(2)).toString();
}
